import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PasteEmailsTab = ({ onEmailsChange, isProcessing }) => {
  const [emailText, setEmailText] = useState('');
  const [emailCount, setEmailCount] = useState(0);
  const [detectedDelimiter, setDetectedDelimiter] = useState('');

  const sampleEmails = `john.doe@example.com
jane.smith@company.org
contact@business.net
support@website.com
info@organization.edu`;

  useEffect(() => {
    const emails = extractEmails(emailText);
    setEmailCount(emails.length);
    onEmailsChange(emails);
    
    // Detect delimiter
    if (emailText.includes(',')) {
      setDetectedDelimiter('comma');
    } else if (emailText.includes(';')) {
      setDetectedDelimiter('semicolon');
    } else if (emailText.includes('\n')) {
      setDetectedDelimiter('newline');
    } else if (emailText.includes(' ')) {
      setDetectedDelimiter('space');
    } else {
      setDetectedDelimiter('');
    }
  }, [emailText, onEmailsChange]);

  const extractEmails = (text) => {
    if (!text.trim()) return [];
    
    // Split by various delimiters and clean up
    const emails = text
      .split(/[,;\n\s]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0)
      .filter((email, index, arr) => arr.indexOf(email) === index); // Remove duplicates
    
    return emails;
  };

  const handleTextChange = (e) => {
    setEmailText(e.target.value);
  };

  const handleClearAll = () => {
    setEmailText('');
    setEmailCount(0);
    setDetectedDelimiter('');
  };

  const handleLoadSample = () => {
    setEmailText(sampleEmails);
  };

  const getDelimiterText = () => {
    switch (detectedDelimiter) {
      case 'comma':
        return 'Comma-separated';
      case 'semicolon':
        return 'Semicolon-separated';
      case 'newline':
        return 'Line-separated';
      case 'space':
        return 'Space-separated';
      default:
        return 'No delimiter detected';
    }
  };

  return (
    <div className="space-y-6">
      {/* Email Input Area */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-text-primary">
            Email Addresses
          </label>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleLoadSample}
              iconName="FileText"
              iconPosition="left"
              disabled={isProcessing}
            >
              Load Sample
            </Button>
            {emailText && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                iconName="X"
                iconPosition="left"
                disabled={isProcessing}
              >
                Clear All
              </Button>
            )}
          </div>
        </div>

        <div className="relative">
          <textarea
            value={emailText}
            onChange={handleTextChange}
            placeholder="Paste your email addresses here...&#10;&#10;You can separate emails with:&#10;• New lines (one per line)&#10;• Commas (email1@example.com, email2@example.com)&#10;• Semicolons (email1@example.com; email2@example.com)&#10;• Spaces (email1@example.com email2@example.com)"
            className="w-full h-64 p-4 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
            disabled={isProcessing}
          />
          
          {/* Character count overlay */}
          <div className="absolute bottom-3 right-3 text-xs text-text-secondary bg-surface px-2 py-1 rounded border">
            {emailText.length.toLocaleString()} characters
          </div>
        </div>
      </div>

      {/* Email Analysis */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-surface border border-border rounded-lg p-4 text-center">
          <div className="flex items-center justify-center w-10 h-10 mx-auto bg-primary/10 rounded-full mb-2">
            <Icon name="Mail" size={20} className="text-primary" />
          </div>
          <div className="text-lg font-semibold text-text-primary">{emailCount.toLocaleString()}</div>
          <div className="text-xs text-text-secondary">Emails Detected</div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4 text-center">
          <div className="flex items-center justify-center w-10 h-10 mx-auto bg-accent/10 rounded-full mb-2">
            <Icon name="Separator" size={20} className="text-accent" />
          </div>
          <div className="text-sm font-medium text-text-primary">{getDelimiterText()}</div>
          <div className="text-xs text-text-secondary">Format Detected</div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4 text-center">
          <div className="flex items-center justify-center w-10 h-10 mx-auto bg-success/10 rounded-full mb-2">
            <Icon name="CheckCircle" size={20} className="text-success" />
          </div>
          <div className="text-sm font-medium text-text-primary">
            {emailCount > 0 ? 'Ready' : 'Waiting'}
          </div>
          <div className="text-xs text-text-secondary">Validation Status</div>
        </div>
      </div>

      {/* Format Guidelines */}
      <div className="bg-surface border border-border rounded-lg p-4">
        <h4 className="text-sm font-medium text-text-primary mb-3 flex items-center">
          <Icon name="Info" size={16} className="mr-2 text-accent" />
          Supported Input Formats
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-text-primary">Delimiters:</h5>
            <ul className="space-y-1 text-xs text-text-secondary">
              <li className="flex items-center space-x-2">
                <Icon name="ArrowRight" size={12} />
                <span>New lines (one email per line)</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="ArrowRight" size={12} />
                <span>Commas (email1, email2)</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="ArrowRight" size={12} />
                <span>Semicolons (email1; email2)</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="ArrowRight" size={12} />
                <span>Spaces (email1 email2)</span>
              </li>
            </ul>
          </div>
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-text-primary">Features:</h5>
            <ul className="space-y-1 text-xs text-text-secondary">
              <li className="flex items-center space-x-2">
                <Icon name="Check" size={12} className="text-success" />
                <span>Automatic duplicate removal</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="Check" size={12} className="text-success" />
                <span>Mixed delimiter support</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="Check" size={12} className="text-success" />
                <span>Real-time email counting</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="Check" size={12} className="text-success" />
                <span>Format detection</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasteEmailsTab;